// Time to Ship (Feature Velocity) detail — the headline tile that replaces Total
// Spend. "Of the features shipped in this window, how long did each take to go
// from idea to merged?" The unit is the feature (an epic-grade grouping of PRs),
// dated at the moment its last PR merged.
//
// Everything below is drawn from /api/time-to-ship, which is served from a
// synthetic population today (see apps/server/src/mock/time-to-ship.ts). The
// DURATIONS are fabricated; the LABELS (feature title, repo, author, team, ticket
// key) are real, so the drill-in table agrees with the Artifacts/Sessions tabs.
//
// Four panels:
//   1. Stage breakdown (hero) — mean hours in each of the four ship stages
//      (Idea, Coding, Pickup, Review), stacked, one bar for the AI-assisted
//      cohort and one for human-only. The AI bar is visibly shorter, and the
//      saving is concentrated in Idea + Coding (the machine-owned stages) — the
//      "AI ships faster" story, made legible rather than asserted.
//   2. Trend — median ship time per cohort bucket, as a line (or one line per
//      dimension value once a breakdown is picked). Lower is better, so the axis
//      is inverted-intuition: a falling line is the team getting faster.
//   3. Breakdown — median ship time per dimension value, fastest-first, with the
//      window's overall median as a dashed reference.
//   4. Shipped features — the drill-in: every feature in the window with its real
//      title/repo/ticket, author, per-stage timings and total ship time.
import { state, $, esc, num, get, autoBucket, scopeQs, CX_LABELS, SR_PALETTE } from '../core'
import { msHtml, msWire } from '../multiselect'
import { paintKpis } from '../kpis'

// Latest payload, kept so the legend/table can repaint without a refetch.
var ttsData: any = null
var ttsReqId = 0

// Per-stage colours for the stacked hero bar — a warm→cool ramp so the four
// segments read as an ordered pipeline, not a category set.
var STAGE_COLORS: Record<string, string> = {
  idea: '#7d5ba6',   // violet — the human scoping stage
  coding: '#0f7a55', // green — the build
  pickup: '#b8860b', // amber — waiting for a reviewer
  review: '#3b6ea5', // blue — in review
}
var STAGE_ORDER = ['idea', 'coding', 'pickup', 'review']
var STAGE_LABELS: Record<string, string> = { idea: 'Idea', coding: 'Coding', pickup: 'Pickup', review: 'Review' }

var DIMS = [
  ['', 'none'],
  ['repo', 'Repository'],
  ['author', 'Author'],
  ['team', 'Team'],
  ['complexity', 'Complexity'],
]

function ttsWinLabel() {
  return state.days === 'all' ? 'all time' : 'last ' + state.days + ' days'
}

// Hours → a human duration. Sub-day figures read in hours (a 6h ship is the
// point of the demo); day-scale in days with one decimal. Shared by the tile,
// the axis, the bars and the table so a number means the same thing everywhere.
export function dur(hours) {
  if (hours == null) return '—'
  if (hours < 1) return Math.round(hours * 60) + 'm'
  if (hours < 24) return (Math.round(hours * 10) / 10) + 'h'
  return (Math.round((hours / 24) * 10) / 10) + 'd'
}

// Series colour by position, matching the rest of the dashboard's palette so a
// value keeps one colour across the trend and the breakdown.
function ttsColor(i) { return SR_PALETTE[i % SR_PALETTE.length] }

function ttsSeriesLabel(label) {
  return state.tts.by === 'complexity' ? (CX_LABELS[label] || label) : label
}

export function renderTimeToShip() {
  var win = esc(ttsWinLabel())
  $('#metric-detail').innerHTML =
    '<div class="tts-controls" id="tts-controls"></div>' +
    '<div class="panel">' +
      '<div class="panel-head"><h2>Where the time goes <span class="metric-sub">' + win +
        ' &middot; mean hours per stage</span></h2></div>' +
      '<div id="tts-stages"></div>' +
      '<div class="card-note" id="tts-stages-note"></div>' +
    '</div>' +
    '<div class="panel">' +
      '<div class="panel-head">' +
        '<h2>Ship time over time <span class="metric-sub">' + win + ' &middot; median idea → merged</span></h2>' +
        '<span class="seg" id="tts-bucket"></span>' +
      '</div>' +
      '<div id="tts-trend"></div>' +
      '<div class="sr-legend" id="tts-trend-legend"></div>' +
      '<div class="card-note" id="tts-trend-note"></div>' +
    '</div>' +
    '<div class="panel" id="tts-bd-panel">' +
      '<div class="panel-head"><h2 id="tts-bd-title">Ship time by dimension</h2></div>' +
      '<div id="tts-bd"></div>' +
      '<div class="card-note" id="tts-bd-note"></div>' +
    '</div>' +
    '<div class="panel">' +
      '<div class="panel-head"><h2>Shipped features <span class="metric-sub" id="tts-tbl-sub"></span></h2></div>' +
      '<div id="tts-tbl"></div>' +
    '</div>'
  renderTtsControls()
  loadTimeToShip()
}

export function renderTtsControls() {
  var el = $('#tts-controls')
  if (!el) return
  var byOpts = DIMS.map(function (o) {
    return '<option value="' + o[0] + '"' + (o[0] === state.tts.by ? ' selected' : '') + '>' + esc(o[1]) + '</option>'
  }).join('')
  // One row: the complexity multiselect on the left, the breakdown select pushed
  // to the right edge (margin-left:auto) — the same "filters left, dimension
  // right" shape the other metric sections use.
  el.innerHTML =
    '<div class="sr-ctrl-row">' +
      msHtml('tts-cx', 'Complexity', (state.tts.complexity || []).length) +
      '<span class="ctl-right"><label class="sel-inline"><span>Break down</span>' +
        '<select class="sr-by" id="tts-by">' + byOpts + '</select></label></span>' +
    '</div>'

  $('#tts-by').onchange = function () { state.tts.by = $('#tts-by').value; loadTimeToShip() }
  msWire({
    id: 'tts-cx',
    options: Object.keys(CX_LABELS).filter(function (k) { return k !== 'none' }).map(function (k) {
    return { value: k, label: CX_LABELS[k] }
  }),
    selected: (state.tts.complexity || []).slice(),
    onChange: function (vals) { state.tts.complexity = vals; loadTimeToShip() },
  })
  var active = autoBucket(state.tts.bucket)
  var bk = ['day', 'week', 'month'].map(function (b) {
    return '<button class="' + (b === active ? 'on' : '') + '" data-b="' + b + '">' + b + '</button>'
  }).join('')
  var bkEl = $('#tts-bucket')
  if (bkEl) {
    bkEl.innerHTML = bk
    Array.prototype.forEach.call(bkEl.children, function (b) {
      b.onclick = function () { state.tts.bucket = b.getAttribute('data-b'); renderTtsControls(); loadTimeToShip() }
    })
  }
}

export function ttsQs(kindOverride?) {
  return 'days=' + encodeURIComponent(String(state.days)) +
    '&bucket=' + encodeURIComponent(autoBucket(state.tts.bucket)) +
    '&by=' + encodeURIComponent(state.tts.by || '') +
    '&complexity=' + encodeURIComponent((state.tts.complexity || []).join(',')) +
    '&kind=' + encodeURIComponent(kindOverride || state.tts.kind || 'ticket') +
    scopeQs()
}

export function loadTimeToShip() {
  var id = ++ttsReqId
  get('/api/time-to-ship?' + ttsQs()).then(function (d) {
    if (id !== ttsReqId) return // a newer request is in flight — drop this one
    ttsData = d
    setTtsKpi(d)
    paintKpis() // the headline tile mirrors the filters applied here
    paintTimeToShip()
    // V2 tile shows BOTH kinds side by side, so also fetch the kind the section
    // is not on — KPI only, the section keeps rendering the active kind. Same
    // reqId guard: a newer main fetch invalidates this one too.
    if (d && d.v2) {
      var other = (d.kind === 'pr') ? 'ticket' : 'pr'
      get('/api/time-to-ship?' + ttsQs(other)).then(function (d2) {
        if (id !== ttsReqId) return
        if (d2 && d2.v2) { setTtsKpi(d2); paintKpis() }
      })
    }
  })
}

// The tile's figure, published from here so the headline can't disagree with the
// section (same window, same filters). Read by kpis.ts. V2 accumulates a KPI
// per kind — the tile shows Tickets and PRs side by side, churn-tile style.
var ttsKpi: any = null
var ttsKpiKinds: any = {}
export function setTtsKpi(d) {
  if (!d) { ttsKpi = null; ttsKpiKinds = {}; return }
  if (d.v2) {
    ttsKpiKinds[d.kind] = d.kpi
    ttsKpi = { v2: true, kind: state.tts.kind, kinds: ttsKpiKinds }
  } else {
    ttsKpi = { kpi: d.kpi }
  }
}
export function getTtsKpi() { return ttsKpi }

function paintTimeToShip() {
  var d = ttsData
  if (!d) return
  // loadTimeToShip also runs in the BACKGROUND (loadKpis fetches it for the
  // headline tile while another metric's detail is open). Only the tile may
  // update then — painting the section would overwrite the open detail, since
  // the v2 paint rewrites #metric-detail wholesale.
  if (state.metric !== 'time_to_ship') return
  if (d.v2) { paintTimeToShipV2(d); return }
  // Hero: one stacked bar for the whole team, segmented by stage. The story is
  // the SHAPE — Idea+Coding (AI-owned) collapsed, Pickup+Review (human-gated)
  // dominate. So the note calls out where the time actually sits.
  $('#tts-stages').innerHTML = stageBars(d.stageBreakdown)
  var stages = d.stageBreakdown || []
  var stageH = function (st) { var s = stages.filter(function (x) { return x.stage === st })[0]; return s ? s.hours : 0 }
  var total = sumStages(stages)
  var build = stageH('idea') + stageH('coding')
  var wait = stageH('pickup') + stageH('review')
  $('#tts-stages-note').innerHTML = (total > 0)
    ? 'With AI writing most of the code, <strong>Idea + Coding</strong> take just <strong>' + esc(dur(build)) +
      '</strong> &mdash; ' + esc(Math.round((build / total) * 100)) + '% of the time to ship. The other <strong>' +
      esc(Math.round((wait / total) * 100)) + '%</strong> is <strong>Pickup + Review</strong> (' + esc(dur(wait)) +
      '): a finished PR waiting for a human. The bottleneck has moved from writing code to getting it reviewed.'
    : 'Mean hours in each stage. No features shipped in this window.'

  var series = state.tts.by ? (d.series || []) : []
  $('#tts-trend').innerHTML = series.length
    ? multiLineChart(d.buckets, series)
    : lineChart(d.buckets, d.points)
  $('#tts-trend-legend').innerHTML = series.length
    ? series.map(function (s, i) {
        return '<span class="leg"><span class="swatch" style="background:' + ttsColor(i) + '"></span>' +
          esc(ttsSeriesLabel(s.label)) + '</span>'
      }).join('')
    : '<span class="leg"><span class="swatch" style="background:' + TTS_GREEN + '"></span>median ship time</span>'
  var cur = d.kpi.current || {}
  $('#tts-trend-note').innerHTML =
    esc(num(cur.count || 0)) + ' features shipped in the ' + esc(ttsWinLabel()) +
    ', median <strong>' + esc(dur(cur.hours)) + '</strong> from idea to merge. ' +
    'Each point is a cohort of features dated at merge; a falling line is the team shipping faster.'

  var bdPanel = $('#tts-bd-panel')
  if (!state.tts.by) {
    bdPanel.style.display = ''
    $('#tts-bd-title').innerHTML = 'Ship time by dimension'
    $('#tts-bd').innerHTML =
      '<div class="dr-bd-prompt">Pick a <strong>Break down by</strong> dimension above to split ship time &mdash; ' +
      'by repo, author, team or complexity. The trend above splits with it.</div>'
    $('#tts-bd-note').innerHTML = ''
  } else {
    var label = (DIMS.filter(function (o) { return o[0] === state.tts.by })[0] || ['', ''])[1]
    $('#tts-bd-title').innerHTML = 'Ship time by ' + esc(String(label).toLowerCase()) +
      ' <span class="metric-sub">' + esc(ttsWinLabel()) + ' &middot; fastest first</span>'
    $('#tts-bd').innerHTML = breakdownBars(d.breakdown, (d.kpi.current || {}).hours)
    $('#tts-bd-note').innerHTML = state.tts.by === 'complexity'
      ? 'Complexity is the feature\'s total churned lines, banded the same way as the filter above.'
      : ''
  }

  $('#tts-tbl-sub').innerHTML = esc(ttsWinLabel()) + ' &middot; ' + esc(num((d.features || []).length)) + ' shipped'
  $('#tts-tbl').innerHTML = featureTable(d.features || [])
}

var TTS_GREEN = '#0f7a55'
var TTS_GREY = '#ece7dc'

function sumStages(stats) {
  return (stats || []).reduce(function (s, x) { return s + (x.hours || 0) }, 0)
}

// The hero: one horizontal stacked bar for the whole team, split into the four
// stages. Each segment is labelled with its own duration + share right on the
// bar, so the reader sees at a glance that Pickup+Review own the timeline — the
// shape IS the message, no cohort comparison needed.
function stageBars(stats) {
  var total = sumStages(stats)
  if (total <= 0) return '<div class="empty">No features shipped in range.</div>'
  var segs = STAGE_ORDER.map(function (st) {
    var stat = (stats || []).filter(function (x) { return x.stage === st })[0]
    var h = stat ? stat.hours : 0
    var w = (h / total) * 100
    // Only label a segment wide enough to hold text; the rest rely on the legend.
    var inner = w >= 9 ? '<span class="tts-seg-lbl">' + esc(dur(h)) + '</span>' : ''
    return '<div class="tts-seg" style="width:' + w.toFixed(1) + '%;background:' + STAGE_COLORS[st] + '" ' +
      'title="' + esc(STAGE_LABELS[st] + ': ' + dur(h) + ' · ' + Math.round(w) + '%') + '">' + inner + '</div>'
  }).join('')
  var bar = '<div class="tts-stage-row tts-stage-solo">' +
    '<div class="tts-stage-track tts-stage-track-lg">' + segs + '</div>' +
    '<div class="tts-stage-total">' + esc(dur(total)) + '</div>' +
  '</div>'
  var legend = '<div class="sr-legend tts-stage-legend">' + STAGE_ORDER.map(function (st) {
    return '<span class="leg"><span class="swatch" style="background:' + STAGE_COLORS[st] + '"></span>' + esc(STAGE_LABELS[st]) + '</span>'
  }).join('') + '</div>'
  return '<div class="tts-stages">' + bar + '</div>' + legend
}

// Median-ship-time line over the cohort axis. A line (not bars) because ship time
// is a continuous quantity we track over time, and the trend — is it falling? — is
// the reading. Gaps for empty buckets rather than dropping to zero.
function lineChart(buckets, points) {
  if (!buckets || !buckets.length) return '<div class="empty">No features in range.</div>'
  if (!points || !points.some(function (p) { return p && p.count > 0 })) {
    return '<div class="empty">No features shipped in range.</div>'
  }
  var W = 920, H = 240, padL = 52, padR = 12, padT = 16, padB = 28
  var plotW = W - padL - padR, plotH = H - padT - padB, n = buckets.length
  var byB = {}
  points.forEach(function (p) { byB[p.bucket] = p })
  var maxH = 0
  points.forEach(function (p) { if (p.hours != null && p.hours > maxH) maxH = p.hours })
  var top = niceTop(maxH)
  var yOf = function (v) { return padT + (1 - v / top) * plotH }
  var xOf = function (i) { return padL + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW) }
  var svg = '<svg viewBox="0 0 ' + W + ' ' + H + '" width="100%" preserveAspectRatio="xMidYMid meet">'
  ;[0, 0.5, 1].forEach(function (g) {
    var v = top * g, y = yOf(v)
    svg += '<line x1="' + padL + '" y1="' + y + '" x2="' + (W - padR) + '" y2="' + y + '" stroke="' + TTS_GREY + '"/>'
    svg += '<text x="' + (padL - 6) + '" y="' + (y + 3) + '" text-anchor="end">' + esc(dur(v)) + '</text>'
  })
  // The line, over buckets that have a value; gaps break the path.
  var d = '', started = false
  var dots = ''
  buckets.forEach(function (b, i) {
    var p = byB[b]
    if (p && p.hours != null) {
      var x = xOf(i), y = yOf(p.hours)
      d += (started ? ' L' : 'M') + x.toFixed(1) + ' ' + y.toFixed(1)
      started = true
      dots += '<circle cx="' + x.toFixed(1) + '" cy="' + y.toFixed(1) + '" r="3" fill="' + TTS_GREEN + '">' +
        '<title>' + esc(b + ': ' + dur(p.hours) + ' median · ' + p.count + ' shipped') + '</title></circle>'
    } else {
      started = false
    }
  })
  svg += '<path d="' + d + '" fill="none" stroke="' + TTS_GREEN + '" stroke-width="2"/>' + dots
  var step = Math.ceil(n / 9)
  buckets.forEach(function (b, i) {
    if (i % step === 0) {
      svg += '<text x="' + xOf(i).toFixed(1) + '" y="' + (H - padB + 14) + '" text-anchor="middle">' + esc(b) + '</text>'
    }
  })
  svg += '</svg>'
  return svg
}

// Breakdown mode: one line per dimension value, sharing the hour axis. Answers
// "who ships fastest, and is the gap widening" over time.
function multiLineChart(buckets, series) {
  if (!buckets || !buckets.length) return '<div class="empty">No features in range.</div>'
  var any = series.some(function (s) { return (s.points || []).some(function (p) { return p && p.count > 0 }) })
  if (!any) return '<div class="empty">No features shipped in range.</div>'
  var W = 920, H = 240, padL = 52, padR = 12, padT = 16, padB = 28
  var plotW = W - padL - padR, plotH = H - padT - padB, n = buckets.length
  var maxH = 0
  series.forEach(function (s) { (s.points || []).forEach(function (p) { if (p.hours != null && p.hours > maxH) maxH = p.hours }) })
  var top = niceTop(maxH)
  var yOf = function (v) { return padT + (1 - v / top) * plotH }
  var xOf = function (i) { return padL + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW) }
  var svg = '<svg viewBox="0 0 ' + W + ' ' + H + '" width="100%" preserveAspectRatio="xMidYMid meet">'
  ;[0, 0.5, 1].forEach(function (g) {
    var v = top * g, y = yOf(v)
    svg += '<line x1="' + padL + '" y1="' + y + '" x2="' + (W - padR) + '" y2="' + y + '" stroke="' + TTS_GREY + '"/>'
    svg += '<text x="' + (padL - 6) + '" y="' + (y + 3) + '" text-anchor="end">' + esc(dur(v)) + '</text>'
  })
  series.forEach(function (s, si) {
    var idx = {}
    ;(s.points || []).forEach(function (p) { idx[p.bucket] = p })
    var d = '', started = false, dots = ''
    buckets.forEach(function (b, i) {
      var p = idx[b]
      if (p && p.hours != null) {
        var x = xOf(i), y = yOf(p.hours)
        d += (started ? ' L' : 'M') + x.toFixed(1) + ' ' + y.toFixed(1)
        started = true
        dots += '<circle cx="' + x.toFixed(1) + '" cy="' + y.toFixed(1) + '" r="2.5" fill="' + ttsColor(si) + '">' +
          '<title>' + esc(b + ' · ' + ttsSeriesLabel(s.label) + ': ' + dur(p.hours) + ' · ' + p.count + ' shipped') + '</title></circle>'
      } else { started = false }
    })
    svg += '<path d="' + d + '" fill="none" stroke="' + ttsColor(si) + '" stroke-width="2"/>' + dots
  })
  var step = Math.ceil(n / 9)
  buckets.forEach(function (b, i) {
    if (i % step === 0) {
      svg += '<text x="' + xOf(i).toFixed(1) + '" y="' + (H - padB + 14) + '" text-anchor="middle">' + esc(b) + '</text>'
    }
  })
  svg += '</svg>'
  return svg
}

// Round an hour maximum up to a clean axis top (in day or hour steps as fits).
function niceTop(maxH) {
  if (maxH <= 0) return 24
  if (maxH < 24) return Math.ceil((maxH * 1.15) / 6) * 6      // 6h steps
  var days = maxH / 24
  return Math.ceil((days * 1.15) / 0.5) * 0.5 * 24            // half-day steps
}

// Horizontal bars, one per dimension value, fastest-first, with the window's
// overall median as a dashed reference. Green because faster is good — the
// opposite hue from defect-rate's red, on purpose.
function breakdownBars(rows, overall) {
  if (!rows || !rows.length) return '<div class="empty">No features in range.</div>'
  var maxH = rows.reduce(function (m, r) { return Math.max(m, r.hours || 0) }, 0) || 1
  var top = maxH * 1.25
  return '<div class="dr-bars">' + rows.map(function (r, i) {
    var label = ttsSeriesLabel(r.label)
    var w = ((r.hours || 0) / top) * 100
    var ref = overall != null ? (overall / top) * 100 : null
    return '<div class="dr-bar-row" title="' + esc(label + ': ' + dur(r.hours) + ' median · ' + r.count + ' features') + '">' +
      '<div class="dr-bar-lbl"><span class="swatch" style="background:' + ttsColor(i) + '"></span>' + esc(label) + '</div>' +
      '<div class="dr-bar-track">' +
        '<div class="dr-bar-fill" style="width:' + w.toFixed(1) + '%;background:' + ttsColor(i) + '"></div>' +
        (ref != null ? '<div class="dr-bar-ref" style="left:' + ref.toFixed(1) + '%"></div>' : '') +
      '</div>' +
      '<div class="dr-bar-val">' + esc(dur(r.hours)) + ' <span class="dr-bar-n">' + esc(num(r.count)) + '</span></div>' +
    '</div>'
  }).join('') + '</div>' +
  (overall != null ? '<div class="card-note">Dashed line: the window\'s overall median (' + esc(dur(overall)) + ').</div>' : '')
}

function featureTable(rows) {
  if (!rows.length) {
    return '<div class="empty">No features shipped in this window &mdash; try a wider window.</div>'
  }
  var head = '<thead><tr><th>Feature</th><th>Author</th><th>Idea</th><th>Coding</th>' +
    '<th>Pickup</th><th>Review</th><th class="dr-right">Ship time</th></tr></thead>'
  var body = '<tbody>' + rows.map(function (r) {
    // Ticket key: a deep link into Jira when the connector gave us a site URL,
    // otherwise plain text. Real link — /browse/<key> on the configured site.
    var ref = r.ticket
      ? (r.ticketUrl
          ? '<a class="dr-art-num tts-ticket-link" href="' + esc(r.ticketUrl) + '" target="_blank" rel="noopener noreferrer">' + esc(r.ticket) + '</a> '
          : '<span class="dr-art-num">' + esc(r.ticket) + '</span> ')
      : ''
    // One-way AI-assisted tag: present when AI made a significant contribution.
    // The absence of the badge is NOT a "human-only" claim — see the mock's doc.
    var aiTag = r.aiAssisted ? ' <span class="tts-chip tts-chip-ai">AI-assisted</span>' : ''
    return '<tr>' +
      '<td>' + ref + esc(r.title) + '<div class="dr-art-repo">' + esc(r.repo) +
        ' &middot; ' + esc(num(r.prCount)) + (r.prCount === 1 ? ' PR' : ' PRs') + aiTag + '</div></td>' +
      '<td>' + esc(r.author) + '<div class="dr-art-repo">' + esc(r.team) + '</div></td>' +
      '<td>' + esc(dur(r.stages.idea)) + '</td>' +
      '<td>' + esc(dur(r.stages.coding)) + '</td>' +
      '<td>' + esc(dur(r.stages.pickup)) + '</td>' +
      '<td>' + esc(dur(r.stages.review)) + '</td>' +
      '<td class="dr-right"><strong>' + esc(dur(r.totalHours)) + '</strong></td>' +
    '</tr>'
  }).join('') + '</tbody>'
  return '<div class="dr-tbl-wrap"><table class="dr-table">' + head + body + '</table></div>'
}

// ---------------------------------------------------------------------------
// V2 (env-gated server-side; the client just follows the payload). Per-artifact
// kind sub-tabs (Tickets | PRs), each with its own 3-stage pipeline:
//   PR:     Coding → Review (two shades: waiting / in review) → Merge
//   Ticket: Waiting to start → In progress → In review
// Panels: funnel ("where the time goes", mean hours per stage with a p90 tick),
// throughput+speed combo (bars = shipped per bucket, line = median ship time),
// breakdown by dimension, and the shipped-artifacts table with per-stage columns.
// The whole section repaints from the payload each fetch — controls included —
// so the sub-tab, stage names and table columns can never disagree with `kind`.
// ---------------------------------------------------------------------------

var V2_KINDS = [['ticket', 'Ticket'], ['pr', 'PR']]

// Leaf-stage colours, shared by the funnel, the table headers and the legend.
// Amber = waiting states, green = the build, blue = review, violet = merge.
var V2_STAGE_COLORS: Record<string, string> = {
  coding: '#0f7a55',
  review_wait: '#b8860b',
  review_active: '#3b6ea5',
  merge: '#7d5ba6',
  waiting: '#b8860b',
  progress: '#0f7a55',
  review: '#3b6ea5',
}

function v2KindNoun(kind, n) {
  if (kind === 'pr') return n === 1 ? 'PR' : 'PRs'
  return n === 1 ? 'ticket' : 'tickets'
}

/** Flat leaf list [{key,label,parent}] for table columns + legend. */
function v2Leaves(stages) {
  var out: any[] = []
  ;(stages || []).forEach(function (s) {
    if (s.parts) s.parts.forEach(function (p) { out.push({ key: p.key, label: p.label, parent: s.label }) })
    else out.push({ key: s.key, label: s.label, parent: '' })
  })
  return out
}

function paintTimeToShipV2(d) {
  var win = esc(ttsWinLabel())
  var noun = v2KindNoun(d.kind, 2)
  // Rebuild the section skeleton (controls included) only when the KIND changed
  // or on first paint. A same-kind repaint (complexity toggle, bucket change)
  // updates the panel bodies in place — rebuilding the controls would close an
  // open multiselect menu mid-pick.
  var ctl = $('#tts-controls')
  var sameKind = ctl && ctl.getAttribute('data-kind') === d.kind
  if (!sameKind) buildTtsV2Skeleton(d, win, noun)
  paintTtsV2Panels(d, win, noun)
}

function buildTtsV2Skeleton(d, win, noun) {
  $('#metric-detail').innerHTML =
    '<div class="tts-controls" id="tts-controls"></div>' +
    '<div class="panel">' +
      '<div class="panel-head">' +
        '<h2>Shipping rate &amp; speed <span class="metric-sub">' + win + ' &middot; bars = ' + esc(noun) +
          ' shipped &middot; line = median ship time</span></h2>' +
        '<span class="seg" id="tts-bucket"></span>' +
      '</div>' +
      '<div id="tts-combo"></div>' +
      '<div class="sr-legend" id="tts-combo-legend"></div>' +
      '<div class="card-note" id="tts-combo-note"></div>' +
    '</div>' +
    '<div class="panel">' +
      '<div class="panel-head"><h2>Where the time goes <span class="metric-sub">' + win +
        ' &middot; mean hours per stage &middot; tick = p90</span></h2></div>' +
      '<div id="tts-funnel"></div>' +
      '<div class="card-note" id="tts-funnel-note"></div>' +
    '</div>' +
    '<div class="panel" id="tts-bd-panel">' +
      '<div class="panel-head"><h2 id="tts-bd-title">Ship time by dimension</h2></div>' +
      '<div id="tts-bd"></div>' +
      '<div class="card-note" id="tts-bd-note"></div>' +
    '</div>' +
    '<div class="panel">' +
      '<div class="panel-head"><h2>Shipped artifacts <span class="metric-sub" id="tts-tbl-sub"></span></h2></div>' +
      '<div id="tts-tbl"></div>' +
    '</div>'
  var ctl = $('#tts-controls')
  if (ctl) ctl.setAttribute('data-kind', d.kind)
  renderTtsControlsV2(d)
}

function paintTtsV2Panels(d, win, noun) {
  $('#tts-funnel').innerHTML = v2Funnel(d.stages, d.stageBreakdown)
  $('#tts-funnel-note').innerHTML = v2Takeaway(d)

  var series = state.tts.by ? (d.series || []) : []
  $('#tts-combo').innerHTML = v2ComboChart(d.buckets, d.points, series)
  var legend = '<span class="leg"><span class="swatch tts-swatch-bar"></span>' + esc(noun) + ' shipped</span>'
  if (series.length) {
    legend += series.map(function (s, i) {
      return '<span class="leg"><span class="swatch" style="background:' + ttsColor(i) + '"></span>' +
        esc(ttsSeriesLabel(s.label)) + '</span>'
    }).join('')
  } else {
    legend += '<span class="leg"><span class="swatch" style="background:' + TTS_GREEN + '"></span>median ship time</span>'
  }
  $('#tts-combo-legend').innerHTML = legend
  var cur = d.kpi.current || {}
  var prev = d.kpi.previous || {}
  var perWeek = bucketsSpanWeeks(d.buckets, state.tts.bucket)
  var rate = perWeek > 0 ? Math.round(((cur.count || 0) / perWeek) * 10) / 10 : null
  $('#tts-combo-note').innerHTML =
    '<strong>' + esc(num(cur.count || 0)) + '</strong> ' + esc(v2KindNoun(d.kind, cur.count || 0)) + ' shipped in the ' +
    esc(ttsWinLabel()) + (rate != null ? ' (&asymp;' + esc(String(rate)) + '/week)' : '') +
    ', median <strong>' + esc(dur(cur.hours)) + '</strong> each' +
    (prev.count != null ? ' &middot; prior window: ' + esc(num(prev.count)) + ' at ' + esc(dur(prev.hours)) : '') +
    '. Rising bars with a flat or falling line is the healthy shape: shipping more without slowing down.'

  var bdPanel = $('#tts-bd-panel')
  if (!state.tts.by) {
    bdPanel.style.display = ''
    $('#tts-bd-title').innerHTML = 'Ship time by dimension'
    $('#tts-bd').innerHTML =
      '<div class="dr-bd-prompt">Pick a <strong>Break down by</strong> dimension above to split ship time &mdash; ' +
      'by repo, author, team or complexity. The trend above splits with it.</div>'
    $('#tts-bd-note').innerHTML = ''
  } else {
    var label = (DIMS.filter(function (o) { return o[0] === state.tts.by })[0] || ['', ''])[1]
    $('#tts-bd-title').innerHTML = 'Ship time by ' + esc(String(label).toLowerCase()) +
      ' <span class="metric-sub">' + esc(ttsWinLabel()) + ' &middot; fastest first</span>'
    $('#tts-bd').innerHTML = breakdownBars(d.breakdown, (d.kpi.current || {}).hours)
    $('#tts-bd-note').innerHTML = state.tts.by === 'complexity'
      ? 'Complexity is the artifact\'s total churned lines, banded the same way as the filter above.'
      : ''
  }

  $('#tts-tbl-sub').innerHTML = esc(ttsWinLabel()) + ' &middot; ' + esc(num((d.artifacts || []).length)) + ' shipped'
  $('#tts-tbl').innerHTML = v2ArtifactTable(d.kind, d.stages, d.artifacts || [])
}

// V2 controls: the kind sub-tab leads the row (it re-words the whole section),
// then the complexity filter, then the breakdown select on the right edge.
function renderTtsControlsV2(d) {
  var el = $('#tts-controls')
  if (!el) return
  var tabs = V2_KINDS.map(function (k) {
    return '<button class="' + (k[0] === state.tts.kind ? 'on' : '') + '" data-k="' + k[0] + '">' + esc(k[1]) + '</button>'
  }).join('')
  var byOpts = DIMS.map(function (o) {
    return '<option value="' + o[0] + '"' + (o[0] === state.tts.by ? ' selected' : '') + '>' + esc(o[1]) + '</option>'
  }).join('')
  // Same chrome as Cost-per-artifact's section-scoping toggle: "Artifact" label
  // + the prominent seg-primary variant (emerald active fill).
  el.innerHTML =
    '<div class="sr-ctrl-row">' +
      '<span class="sr-lbl">Artifact</span><span class="seg seg-primary tts-kind" id="tts-kind">' + tabs + '</span>' +
      '<span style="margin-left:18px">' + msHtml('tts-cx', 'Complexity', (state.tts.complexity || []).length) + '</span>' +
      '<span class="ctl-right"><label class="sel-inline"><span>Break down</span>' +
        '<select class="sr-by" id="tts-by">' + byOpts + '</select></label></span>' +
    '</div>'
  Array.prototype.forEach.call($('#tts-kind').children, function (b) {
    b.onclick = function () {
      if (b.getAttribute('data-k') === state.tts.kind) return
      state.tts.kind = b.getAttribute('data-k')
      loadTimeToShip()
    }
  })
  $('#tts-by').onchange = function () { state.tts.by = $('#tts-by').value; loadTimeToShip() }
  msWire({
    id: 'tts-cx',
    options: Object.keys(CX_LABELS).filter(function (k) { return k !== 'none' }).map(function (k) {
    return { value: k, label: CX_LABELS[k] }
  }),
    selected: (state.tts.complexity || []).slice(),
    onChange: function (vals) { state.tts.complexity = vals; loadTimeToShip() },
  })
  var active = autoBucket(state.tts.bucket)
  var bkEl = $('#tts-bucket')
  if (bkEl) {
    bkEl.innerHTML = ['day', 'week', 'month'].map(function (b) {
      return '<button class="' + (b === active ? 'on' : '') + '" data-b="' + b + '">' + b + '</button>'
    }).join('')
    Array.prototype.forEach.call(bkEl.children, function (b) {
      b.onclick = function () {
        state.tts.bucket = b.getAttribute('data-b')
        // Same-kind repaints keep the controls, so the highlight moves here.
        Array.prototype.forEach.call(bkEl.children, function (x) { x.className = x === b ? 'on' : '' })
        loadTimeToShip()
      }
    })
  }
}

// The funnel: one horizontal row per TOP-LEVEL stage, top-to-bottom in pipeline
// order. Bar length = mean hours on a shared scale; a stage with parts (PR
// review) draws its shades side by side inside one bar. A thin tick marks the
// p90 — the slow tail a mean hides. Each row carries its own duration + share,
// so the biggest row is legible without a legend.
function v2Funnel(stages, stats) {
  var byKey = {}
  ;(stats || []).forEach(function (s) { byKey[s.stage] = s })
  var total = (stats || []).reduce(function (s, x) { return s + (x.hours || 0) }, 0)
  if (total <= 0) return '<div class="empty">Nothing shipped in range.</div>'
  var scale = 0
  ;(stats || []).forEach(function (s) { scale = Math.max(scale, s.p90 || 0, s.hours || 0) })
  scale = scale || 1
  return '<div class="tts-funnel">' + (stages || []).map(function (meta) {
    var st = byKey[meta.key] || { hours: 0, p90: 0 }
    var share = Math.round(((st.hours || 0) / total) * 100)
    var segs = ''
    if (meta.parts && st.parts) {
      segs = meta.parts.map(function (p) {
        var h = st.parts[p.key] || 0
        var w = (h / scale) * 100
        return '<div class="tts-fn-seg" style="width:' + w.toFixed(1) + '%;background:' + V2_STAGE_COLORS[p.key] + '" ' +
          'title="' + esc(p.label + ': ' + dur(h) + ' mean') + '"></div>'
      }).join('')
    } else {
      var w = ((st.hours || 0) / scale) * 100
      segs = '<div class="tts-fn-seg" style="width:' + w.toFixed(1) + '%;background:' + V2_STAGE_COLORS[meta.key] + '"></div>'
    }
    var tick = st.p90 > 0
      ? '<div class="tts-fn-p90" style="left:' + ((st.p90 / scale) * 100).toFixed(1) + '%" title="' + esc('p90: ' + dur(st.p90)) + '"></div>'
      : ''
    var subLbl = meta.parts && st.parts
      ? '<div class="tts-fn-parts">' + meta.parts.map(function (p) {
          return '<span><span class="swatch" style="background:' + V2_STAGE_COLORS[p.key] + '"></span>' +
            esc(p.label.toLowerCase() + ' ' + dur(st.parts[p.key] || 0)) + '</span>'
        }).join('') + '</div>'
      : ''
    return '<div class="tts-fn-row" title="' + esc(meta.label + ': ' + dur(st.hours) + ' mean · ' + share + '% of ship time · p90 ' + dur(st.p90)) + '">' +
      '<div class="tts-fn-lbl">' + esc(meta.label) + '</div>' +
      '<div class="tts-fn-track">' + segs + tick + '</div>' +
      '<div class="tts-fn-val">' + esc(dur(st.hours)) + ' <span class="tts-fn-share">' + esc(String(share)) + '%</span></div>' +
    '</div>' + subLbl
  }).join('') + '</div>'
}

// The one-line takeaway, computed from the numbers rather than asserted: name
// the stage that owns the pipeline, and for PR review say which shade owns it.
function v2Takeaway(d) {
  var stats = d.stageBreakdown || []
  var total = stats.reduce(function (s, x) { return s + (x.hours || 0) }, 0)
  if (total <= 0) return 'Mean hours in each stage. Nothing shipped in this window.'
  var top: any = null
  stats.forEach(function (s) { if (!top || (s.hours || 0) > top.hours) top = s })
  var meta = (d.stages || []).filter(function (s) { return s.key === top.stage })[0] || { label: top.stage }
  var share = Math.round((top.hours / total) * 100)
  var text = 'Most of the time to ship is <strong>' + esc(meta.label) + '</strong> &mdash; ' +
    esc(dur(top.hours)) + ' mean, ' + esc(String(share)) + '% of the pipeline.'
  if (top.parts) {
    var wait = top.parts.review_wait || 0
    var active = top.parts.review_active || 0
    if (wait > active) {
      text += ' Most of that is <strong>waiting for a first review</strong> (' + esc(dur(wait)) + ' of ' +
        esc(dur(top.hours)) + ') &mdash; a finished PR sitting in a queue.'
    }
  }
  return text
}

// Weeks spanned by the axis, for the ≈N/week rate in the note.
function bucketsSpanWeeks(buckets, bucket) {
  var n = (buckets || []).length
  if (!n) return 0
  var b = autoBucket(bucket)
  if (b === 'week') return n
  if (b === 'month') return n * 4.35
  return n / 7
}

// Throughput + speed in one chart: count bars per bucket on a right-hand count
// axis, the median-ship-time line (or one line per breakdown value) on the left
// hour axis. Bars stay TOTAL even in breakdown mode — the rate is a property of
// the team; the lines are where the split lives.
function v2ComboChart(buckets, points, series) {
  if (!buckets || !buckets.length) return '<div class="empty">Nothing in range.</div>'
  var anyBar = (points || []).some(function (p) { return p && p.count > 0 })
  if (!anyBar) return '<div class="empty">Nothing shipped in range.</div>'
  var W = 920, H = 260, padL = 52, padR = 46, padT = 16, padB = 28
  var plotW = W - padL - padR, plotH = H - padT - padB, n = buckets.length
  var byB = {}
  ;(points || []).forEach(function (p) { byB[p.bucket] = p })
  var maxC = 0, maxH = 0
  ;(points || []).forEach(function (p) {
    if (p.count > maxC) maxC = p.count
    if (p.hours != null && p.hours > maxH) maxH = p.hours
  })
  if (series && series.length) {
    series.forEach(function (s) { (s.points || []).forEach(function (p) { if (p.hours != null && p.hours > maxH) maxH = p.hours }) })
  }
  var topH = niceTop(maxH)
  var topC = Math.max(2, Math.ceil(maxC * 1.2))
  var yOfH = function (v) { return padT + (1 - v / topH) * plotH }
  var yOfC = function (v) { return padT + (1 - v / topC) * plotH }
  var xOf = function (i) { return padL + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW) }
  var slot = plotW / Math.max(1, n)
  var barW = Math.max(3, Math.min(26, slot * 0.55))
  var svg = '<svg viewBox="0 0 ' + W + ' ' + H + '" width="100%" preserveAspectRatio="xMidYMid meet">'
  ;[0, 0.5, 1].forEach(function (g) {
    var y = yOfH(topH * g)
    svg += '<line x1="' + padL + '" y1="' + y + '" x2="' + (W - padR) + '" y2="' + y + '" stroke="' + TTS_GREY + '"/>'
    svg += '<text x="' + (padL - 6) + '" y="' + (y + 3) + '" text-anchor="end">' + esc(dur(topH * g)) + '</text>'
    svg += '<text x="' + (W - padR + 6) + '" y="' + (y + 3) + '" text-anchor="start">' + esc(String(Math.round(topC * g))) + '</text>'
  })
  // Bars first (behind the line), on the count axis.
  buckets.forEach(function (b, i) {
    var p = byB[b]
    if (!p || !p.count) return
    var x = xOf(i) - barW / 2
    var y = yOfC(p.count)
    svg += '<rect class="tts-thr-bar" x="' + x.toFixed(1) + '" y="' + y.toFixed(1) + '" width="' + barW.toFixed(1) +
      '" height="' + (H - padB - y).toFixed(1) + '" rx="2">' +
      '<title>' + esc(b + ': ' + p.count + ' shipped') + '</title></rect>'
  })
  // The line(s), on the hour axis.
  var drawLine = function (pts, color, label) {
    var idx = {}
    ;(pts || []).forEach(function (p) { idx[p.bucket] = p })
    var dPath = '', started = false, dots = ''
    buckets.forEach(function (b, i) {
      var p = idx[b]
      if (p && p.hours != null) {
        var x = xOf(i), y = yOfH(p.hours)
        dPath += (started ? ' L' : 'M') + x.toFixed(1) + ' ' + y.toFixed(1)
        started = true
        dots += '<circle cx="' + x.toFixed(1) + '" cy="' + y.toFixed(1) + '" r="2.8" fill="' + color + '">' +
          '<title>' + esc(b + (label ? ' · ' + label : '') + ': ' + dur(p.hours) + ' median · ' + p.count + ' shipped') + '</title></circle>'
      } else { started = false }
    })
    return '<path d="' + dPath + '" fill="none" stroke="' + color + '" stroke-width="2"/>' + dots
  }
  if (series && series.length) {
    series.forEach(function (s, si) { svg += drawLine(s.points, ttsColor(si), ttsSeriesLabel(s.label)) })
  } else {
    svg += drawLine(points, TTS_GREEN, '')
  }
  var step = Math.ceil(n / 9)
  buckets.forEach(function (b, i) {
    if (i % step === 0) {
      svg += '<text x="' + xOf(i).toFixed(1) + '" y="' + (H - padB + 14) + '" text-anchor="middle">' + esc(b) + '</text>'
    }
  })
  svg += '</svg>'
  return svg
}

// Shipped artifacts: one stage column per LEAF (so PR review shows its waiting
// and active halves separately), plus the total. Ticket keys deep-link to Jira.
function v2ArtifactTable(kind, stages, rows) {
  if (!rows.length) {
    return '<div class="empty">Nothing shipped in this window &mdash; try a wider window.</div>'
  }
  var leaves = v2Leaves(stages)
  var head = '<thead><tr><th>' + (kind === 'pr' ? 'PR' : 'Ticket') + '</th><th>Author</th>' +
    leaves.map(function (l) { return '<th>' + esc(l.label) + '</th>' }).join('') +
    '<th class="dr-right">Ship time</th></tr></thead>'
  var body = '<tbody>' + rows.map(function (r) {
    var ref = r.ticket
      ? (r.ticketUrl
          ? '<a class="dr-art-num tts-ticket-link" href="' + esc(r.ticketUrl) + '" target="_blank" rel="noopener noreferrer">' + esc(r.ticket) + '</a> '
          : '<span class="dr-art-num">' + esc(r.ticket) + '</span> ')
      : ''
    var aiTag = r.aiAssisted ? ' <span class="tts-chip tts-chip-ai">AI-assisted</span>' : ''
    return '<tr>' +
      '<td>' + ref + esc(r.title) + '<div class="dr-art-repo">' + esc(r.repo) + aiTag + '</div></td>' +
      '<td>' + esc(r.author) + '<div class="dr-art-repo">' + esc(r.team) + '</div></td>' +
      leaves.map(function (l) { return '<td>' + esc(dur((r.stages || {})[l.key])) + '</td>' }).join('') +
      '<td class="dr-right"><strong>' + esc(dur(r.totalHours)) + '</strong></td>' +
    '</tr>'
  }).join('') + '</tbody>'
  return '<div class="dr-tbl-wrap"><table class="dr-table">' + head + body + '</table></div>'
}
